/**
 * Facebook / Google merge-confirmation page.
 *
 * Reached from the Facebook OAuth callback when the signed-in email
 * already belongs to an existing agent (typically Google-linked). The
 * page shows both sides side-by-side and lets the user either:
 *   - **Link accounts** — merge Facebook credentials into the existing
 *     profile and drop them at their dashboard.
 *   - **Cancel** — throw the pending merge away and go home.
 *
 * All server-side auth work happens inside `/api/facebook-auth/link-*`;
 * this page only orchestrates the two clicks.
 */
import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { getApiUrl } from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Loader2, Link2, AlertTriangle } from "lucide-react";

const API = getApiUrl();

export default function LinkAccountPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) { setError("Missing link token"); setLoading(false); return; }
    let cancelled = false;
    axios
      .get(`${API}/facebook-auth/link-preview`, { params: { token } })
      .then((r) => { if (!cancelled) setPreview(r.data); })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.detail || "Could not load link request"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  const linkNow = () => {
    // Backend returns a 302 → follow it so the session cookie sticks and
    // the browser lands on /property-admin/dashboard?facebook=linked.
    setBusy(true);
    window.location.href = `${API}/facebook-auth/link-confirm?token=${encodeURIComponent(token)}`;
  };

  const decline = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/facebook-auth/link-decline`, null, { params: { token } });
    } catch { /* ignore — we're leaving anyway */ }
    navigate("/", { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="link-account-loading">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30" data-testid="link-account-error">
        <div className="glass-card p-8 max-w-md w-full text-center space-y-4">
          <AlertTriangle className="h-8 w-8 mx-auto text-amber-500" />
          <p className="text-sm text-foreground">{error}</p>
          <Button asChild variant="outline"><Link to="/property-admin/login">Back to sign in</Link></Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 px-4">
      <div className="glass-card p-8 max-w-lg w-full space-y-6" data-testid="link-account-page">
        <div className="text-center space-y-2">
          <Link2 className="h-10 w-10 mx-auto text-primary" />
          <h1 className="font-serif text-2xl">You already have an account</h1>
          <p className="text-sm text-muted-foreground">
            An agent profile with <strong data-testid="link-account-email">{preview.email}</strong> already exists
            {preview.existing_provider ? <> (signed up via <strong className="capitalize">{preview.existing_provider}</strong>)</> : null}.
            Link your Facebook sign-in to it so you can use either provider going forward?
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="border border-border p-3 rounded space-y-1">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Existing profile</div>
            <div className="font-medium truncate" data-testid="link-account-existing">{preview.existing_name || preview.email}</div>
            <div className="text-xs text-muted-foreground capitalize">{preview.existing_provider || "email"}</div>
          </div>
          <div className="border border-border p-3 rounded space-y-1">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Signing in with</div>
            <div className="font-medium truncate flex items-center gap-2">
              {preview.fb_picture && (
                <img src={preview.fb_picture} alt="" className="h-5 w-5 rounded-full object-cover slh-no-img-style" />
              )}
              <span data-testid="link-account-fb">{preview.fb_name || preview.email}</span>
            </div>
            <div className="text-xs text-muted-foreground">Facebook</div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 pt-2">
          <Button
            onClick={linkNow}
            disabled={busy}
            className="flex-1"
            data-testid="link-account-confirm-btn"
          >
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Link2 className="h-4 w-4 mr-2" />}
            Link accounts
          </Button>
          <Button
            variant="outline"
            onClick={decline}
            disabled={busy}
            className="flex-1"
            data-testid="link-account-decline-btn"
          >
            Cancel
          </Button>
        </div>

        <p className="text-[11px] text-center text-muted-foreground">
          Linking never changes who owns your listings — it just lets you sign in with either provider from now on.
        </p>
      </div>
    </div>
  );
}
